import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { MapArea } from '../../interfaces/services/geography';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { ApiStatus } from '../../utils/enums';

interface MapState {
    areasApiStatus: ApiStatus;
    areas: MapArea[];
}

const initialState: MapState = {
    areasApiStatus: ApiStatus.Idle,
    areas: [],
};

export const fetchAreas = createAsyncThunk(
    'map/fetchAreas',
    async ({
        origin,
        destination,
    }: {
        origin: string;
        destination: string;
    }): Promise<GeoJSON.FeatureCollection<GeoJSON.Geometry>> => {
        try {
            const formData = {
                origin_area: origin,
                destination_area: destination,
            };

            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/api/v1/network/geography/area/geojson`,
                body: formData,
            }) as AxiosResponse<GeoJSON.FeatureCollection<GeoJSON.Geometry>>;

            return data;
        } catch (error) {
            console.error('Unable to retrieve area geojson definitions', error);
            toast.error('Error loading area geography');
            throw error;
        }
    }
);

const mapSlice = createSlice({
    name: 'map',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchAreas.pending, (state) => {
            state.areasApiStatus = ApiStatus.Loading;
            state.areas = [];
        });
        builder.addCase(fetchAreas.fulfilled, (state, action) => {
            state.areasApiStatus = ApiStatus.Success;

            if (action.payload.features?.length > 0) {
                if (action.meta.arg.origin === action.meta.arg.destination) {
                    state.areas = [
                        {
                            geography: action.payload.features[0],
                            id: action.meta.arg.origin,
                            hovered: false,
                        },
                    ];
                } else {
                    state.areas = [
                        {
                            geography: action.payload.features[0],
                            id: action.meta.arg.destination,
                            hovered: false,
                        },
                        {
                            geography: action.payload.features[1],
                            id: action.meta.arg.origin,
                            hovered: false,
                        },
                    ];
                }
            }
        });
        builder.addCase(fetchAreas.rejected, (state) => {
            state.areasApiStatus = ApiStatus.Failure;
            state.areas = [];
        });
    },
});

export default mapSlice.reducer;
